import { useMemo, useState } from 'react';
import {
  Box, Flex, Input, Button, Table, Thead, Tbody, Tr, Th, Td, Text, Badge,
  useColorModeValue, useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchLicenses, createLicense, deleteLicense, type LicenseDto } from '../../../api/libraryApi';
import { renameLicense, mergeLicenses } from '../../../api/dictionariesApi';
import { ApiError } from '../../../services/apiClient';
import { RenameValueModal, MergeValueModal, ConfirmDeleteModal } from './DictionaryModals';

/**
 * The managed-license half of the Dictionary Manager: list licenses (with System / In-use badges),
 * add a new one, and rename / merge / delete. The Streamko system license is protected — it cannot
 * be renamed, deleted, or used as a merge source (it may be a merge target).
 */
export function LicensesSection() {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [active, setActive] = useState<LicenseDto | null>(null);
  const [mode, setMode] = useState<'rename' | 'merge' | 'delete' | null>(null);

  const secondaryColor = useColorModeValue('secondaryGray.600', 'secondaryGray.500');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const { data: licenses, isLoading } = useQuery({ queryKey: ['admin-licenses'], queryFn: fetchLicenses });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['admin-licenses'] });
    queryClient.invalidateQueries({ queryKey: ['admin-library'] });
  }

  function close() { setActive(null); setMode(null); }

  function onError(err: unknown, fallbackKey: string) {
    const message = err instanceof ApiError ? err.message : t(fallbackKey);
    toast({ title: message, status: 'error', duration: 4000, isClosable: true });
  }

  const createMutation = useMutation({
    mutationFn: (value: string) => createLicense(value),
    onSuccess: () => { invalidate(); setName(''); },
    onError: (e) => onError(e, 'admin.licenses.createFailed'),
  });

  const renameMutation = useMutation({
    mutationFn: (newName: string) => renameLicense(active!.id, newName),
    onSuccess: () => {
      invalidate();
      toast({ title: t('admin.dictionary.license.renamed'), status: 'success', duration: 3000, isClosable: true });
      close();
    },
    onError: (e) => onError(e, 'admin.dictionary.rename.failed'),
  });

  const mergeMutation = useMutation({
    mutationFn: (targetId: string) => mergeLicenses(active!.id, targetId),
    onSuccess: (r) => {
      invalidate();
      toast({ title: t('admin.dictionary.applied', { count: r.affected }), status: 'success', duration: 3000, isClosable: true });
      close();
    },
    onError: (e) => onError(e, 'admin.dictionary.merge.failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteLicense(active!.id),
    onSuccess: () => {
      invalidate();
      toast({ title: t('admin.dictionary.license.deleted'), status: 'success', duration: 3000, isClosable: true });
      close();
    },
    onError: (e) => onError(e, 'admin.licenses.deleteFailed'),
  });

  function handleAdd() {
    const trimmed = name.trim();
    if (trimmed) createMutation.mutate(trimmed);
  }

  const mergeOptions = useMemo(
    () => (licenses ?? [])
      .filter((l) => l.id !== active?.id)
      .map((l) => ({ value: l.id, label: l.name })),
    [licenses, active],
  );

  return (
    <Box data-testid="licenses-section">
      <Text fontSize="xs" color={secondaryColor} mb={3} data-testid="licenses-note">
        {t('admin.dictionary.managedNote')}
      </Text>
      <Flex gap={3} mb={5} maxW="480px">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
          placeholder={t('admin.licenses.name')}
          data-testid="license-name-input"
        />
        <Button
          colorScheme="brand"
          onClick={handleAdd}
          isDisabled={!name.trim() || createMutation.isPending}
          isLoading={createMutation.isPending}
          data-testid="btn-add-license"
        >
          {t('admin.licenses.add')}
        </Button>
      </Flex>

      {isLoading ? (
        <Text color={secondaryColor} fontSize="sm">{t('admin.common.loading', 'Loading…')}</Text>
      ) : !licenses || licenses.length === 0 ? (
        <Text color={secondaryColor} fontSize="sm" data-testid="licenses-empty">{t('admin.licenses.empty')}</Text>
      ) : (
        <Table variant="simple" size="sm" data-testid="licenses-table">
          <Thead>
            <Tr>
              <Th color={secondaryColor} borderColor={borderColor}>{t('admin.licenses.name')}</Th>
              <Th color={secondaryColor} borderColor={borderColor}>{t('admin.common.status')}</Th>
              <Th color={secondaryColor} borderColor={borderColor} textAlign="right">{t('admin.dictionary.actions')}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {licenses.map((license) => (
              <Tr key={license.id} data-testid={`license-row-${license.id}`}>
                <Td borderColor={borderColor}>
                  <Text fontSize="sm" fontWeight="600">{license.name}</Text>
                </Td>
                <Td borderColor={borderColor}>
                  <Flex gap={1} flexWrap="wrap">
                    {license.isSystem && (
                      <Badge colorScheme="blue" borderRadius="8px" px="8px" fontSize="xs">
                        {t('admin.licenses.system', 'System')}
                      </Badge>
                    )}
                    {license.inUse && (
                      <Badge colorScheme="orange" borderRadius="8px" px="8px" fontSize="xs">
                        {t('admin.licenses.inUse')}
                      </Badge>
                    )}
                  </Flex>
                </Td>
                <Td borderColor={borderColor} textAlign="right">
                  <Flex gap={2} justify="flex-end">
                    <Button size="xs" variant="outline" isDisabled={license.isSystem}
                      title={license.isSystem ? t('admin.dictionary.license.systemProtected') : undefined}
                      onClick={() => { setActive(license); setMode('rename'); }}
                      data-testid={`license-rename-${license.id}`}>
                      {t('admin.dictionary.rename.action')}
                    </Button>
                    <Button size="xs" variant="outline"
                      isDisabled={license.isSystem || (licenses?.length ?? 0) < 2}
                      title={license.isSystem ? t('admin.dictionary.license.systemProtected') : undefined}
                      onClick={() => { setActive(license); setMode('merge'); }}
                      data-testid={`license-merge-${license.id}`}>
                      {t('admin.dictionary.merge.action')}
                    </Button>
                    <Button size="xs" variant="outline" colorScheme="red"
                      isDisabled={license.isSystem || license.inUse}
                      title={license.isSystem
                        ? t('admin.licenses.systemCannotDelete')
                        : license.inUse ? t('admin.licenses.deleteInUse') : undefined}
                      onClick={() => { setActive(license); setMode('delete'); }}
                      data-testid={`license-delete-${license.id}`}>
                      {t('admin.dictionary.delete.action')}
                    </Button>
                  </Flex>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}

      {active && mode === 'rename' && (
        <RenameValueModal
          isOpen
          onClose={close}
          currentValue={active.name}
          onSubmit={(v) => renameMutation.mutate(v)}
          isPending={renameMutation.isPending}
        />
      )}
      {active && mode === 'merge' && (
        <MergeValueModal
          isOpen
          onClose={close}
          source={active.name}
          options={mergeOptions}
          onSubmit={(targetId) => mergeMutation.mutate(targetId)}
          isPending={mergeMutation.isPending}
        />
      )}
      {active && mode === 'delete' && (
        <ConfirmDeleteModal
          isOpen
          onClose={close}
          value={active.name}
          trackCount={0}
          message={t('admin.dictionary.license.deleteBody', { value: active.name })}
          onConfirm={() => deleteMutation.mutate()}
          isPending={deleteMutation.isPending}
        />
      )}
    </Box>
  );
}
